import * as React from "react";
import { EXPERIENCE, InsuranceProducts } from "../../../constants";
import { QueryStatus, skipToken } from "@reduxjs/toolkit/query";
import { getAccountInfo, useUpdateAccountInfoMutation, } from "../../../services/savvy";
import { selectInsuranceProduct, setAccountReferenceId, setCreateNewPrefillAccountReferenceId, } from "../../applicant/applicantSlice";
import useAccountReferenceId from "../../../services/savvy/useAccountReferenceId";
import useAppDispatch from "../../../hooks/useAppDispatch";
import useAppSelector from "../../../hooks/useAppSelector";
import { useEffectOnceWhen } from "rooks";
export default function PrefillProvider(_a) {
    var children = _a.children;
    var isPrefill = useAppSelector(function (state) { return state.configuration.experience === EXPERIENCE.PREFILL; });
    var didPrefillAuto = useAppSelector(function (_a) {
        var unlinkedApplication = _a.unlinkedApplication;
        return unlinkedApplication.prefill.auto.data &&
            unlinkedApplication.prefill.auto.issuer;
    });
    var didPrefillHome = useAppSelector(function (_a) {
        var unlinkedApplication = _a.unlinkedApplication;
        return unlinkedApplication.prefill.home.data &&
            unlinkedApplication.prefill.home.issuer;
    });
    var dispatch = useAppDispatch();
    var didTriggerInitialRoute = React.useRef(false);
    var hasFeatureFlags = useAppSelector(function (state) { return state.featureFlags.status === QueryStatus.fulfilled; });
    var updateAccountInfo = useUpdateAccountInfoMutation()[0];
    var _b = useAppSelector(function (_a) {
        var _b = _a.applicant, accountReferenceId = _b.accountReferenceId, jornayaLeadId = _b.jornayaLeadId;
        return {
            accountReferenceId: accountReferenceId,
            jornayaLeadId: jornayaLeadId.prefill,
        };
    }), accountReferenceId = _b.accountReferenceId, jornayaLeadId = _b.jornayaLeadId;
    var isCompleteAutoApplication = useAppSelector(function (_a) {
        var unlinkedApplication = _a.unlinkedApplication, applicant = _a.applicant;
        var auto = unlinkedApplication.auto;
        var _b = auto.drivers[0] || {}, firstName = _b.firstName, lastName = _b.lastName, dateOfBirth = _b.dateOfBirth, gender = _b.gender, maritalStatus = _b.maritalStatus;
        var policyHolderDataToCheck = {
            firstName: firstName,
            lastName: lastName,
            dateOfBirth: dateOfBirth,
            gender: gender,
            maritalStatus: maritalStatus,
        };
        if (!applicant.jornayaLeadId.prefill) {
            return false;
        }
        if (Object.values(policyHolderDataToCheck).every(function (val) { return val !== undefined && val !== null && val !== ""; }) === false) {
            return false;
        }
        return (auto.vehicles.length > 0 &&
            auto.vehicles.every(function (veh) { var _a, _b; return ((_a = veh.trim) === null || _a === void 0 ? void 0 : _a.vin) || ((_b = veh.trim) === null || _b === void 0 ? void 0 : _b.vinStub); }));
    });
    var getAccountReferenceId = useAccountReferenceId().getAccountReferenceId;
    var _c = getAccountInfo.useQueryState(accountReferenceId || skipToken), isFetching = _c.isFetching, isLoading = _c.isLoading;
    var isSavvyAgent = useAppSelector(function (state) { return state.configuration.isSavvyAgent; });
    function getShouldCreateNewAccountReferenceId() {
        if (isFetching || isLoading || !isPrefill) {
            return false;
        }
        if (isSavvyAgent) {
            return true;
        }
        return (didPrefillAuto && !isCompleteAutoApplication) || didPrefillHome;
    }
    var shouldCreateNewAccountReferenceId = getShouldCreateNewAccountReferenceId();
    var didPrefill = isPrefill && (didPrefillAuto || didPrefillHome);
    var isBundlePrefill = didPrefillAuto && didPrefillHome;
    var shouldDetermineRoute = hasFeatureFlags &&
        didPrefill &&
        !isBundlePrefill &&
        !didTriggerInitialRoute.current;
    useEffectOnceWhen(function () {
        if (didPrefillHome) {
            dispatch(selectInsuranceProduct({
                product: InsuranceProducts.Home,
                changeRoute: false,
            }));
        }
        didTriggerInitialRoute.current = true;
    }, shouldDetermineRoute);
    // generate a new account reference ID in order to allow for prefill users to edit their
    // account info. currently, the API merges all unlinked account documents into one, preventing editing
    // of prefill information
    // https://linear.app/trellisconnect/issue/SAV-5829/widget-doesnt-delete-information-from-prefill-account
    useEffectOnceWhen(function () {
        getAccountReferenceId()
            .unwrap()
            .then(function (nextARID) {
            dispatch(setCreateNewPrefillAccountReferenceId(true));
            dispatch(setAccountReferenceId(nextARID));
            if (jornayaLeadId) {
                updateAccountInfo({
                    accountReferenceId: nextARID,
                    jornayaLeadId: jornayaLeadId,
                });
            }
        });
    }, shouldCreateNewAccountReferenceId);
    return React.createElement(React.Fragment, null, children);
}
